import React, { useCallback, useState } from 'react';
import { Button, Flex, Input, message, Modal } from 'antd';
import { useToken } from '@/common/helper';
import useUnauthorize from '@/hooks/useUnauthorize';
import { useSpin } from '@/components/SpinContext';
import { PublishReport } from '@/apis/gxpReport';

interface PublishReportModalProps {
  reportID?: string;
  open: boolean;
  action?: 'publish' | 'reject';
  referenceID: string;
  onCancel: () => void;
  handleRefreshPageData?: () => void;
}

const PublishReportModal: React.FC<PublishReportModalProps> = ({
  reportID,
  open,
  action = 'publish',
  referenceID,
  onCancel,
  handleRefreshPageData,
}) => {
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();
  const [notes, setNotes] = useState('');
  const isRejectAction = action === 'reject';
  const isNotesRequiredMissing = isRejectAction && notes.trim().length === 0;
  const isNotesTooLong = notes.length > 300;

  const handlePublishReport = useCallback(() => {
    if (isNotesRequiredMissing || isNotesTooLong) {
      return;
    }

    showSpin();
    const model = {
      action,
      reviewer_note: notes,
    };
    PublishReport(token, reportID, model)
      .then((res) => {
        const data = res?.data?.data?.data;
        if (data) {
          message.success(res?.data?.data?.message);
          onCancel();
          handleRefreshPageData();
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [
    reportID,
    onCancel,
    handleRefreshPageData,
    notes,
    action,
    isNotesRequiredMissing,
    isNotesTooLong,
    token,
    showSpin,
    hideSpin,
    unauthorize,
  ]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      width={460}
      className='gxp-modal'
      destroyOnClose
    >
      <Flex vertical gap={28}>
        <Flex vertical gap={4}>
          <div className='gxp-modal__title'>
            {isRejectAction ? 'Reject report' : 'Publish report'}
          </div>
          <div className='gxp-modal__subtitle'>Shipment {referenceID}</div>
        </Flex>
        <div className='gxp-modal__description'>
          {isRejectAction
            ? 'Rejecting this report will mark it as rejected and log the disposition in the chain of custody.'
            : 'This makes the report available to send to the customer and logs it in the chain of custody.'}
        </div>

        <Flex vertical>
          <div className='gxp-modal__field-label'>
            {isRejectAction ? 'Rejection reason' : 'Optional notes'}
          </div>
          <Input.TextArea
            className='gxp-modal__textarea'
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            status={isNotesTooLong ? 'error' : ''}
          />
          {isNotesTooLong && (
            <div className='gxp-modal__error'>
              Notes cannot exceed 300 characters.
            </div>
          )}
        </Flex>

        <Flex vertical>
          <Button
            type='primary'
            block
            className='gxp-modal__submit-btn'
            disabled={isNotesRequiredMissing || isNotesTooLong}
            onClick={() => handlePublishReport()}
          >
            Submit
          </Button>
          <button
            type='button'
            className='gxp-modal__cancel-link'
            onClick={onCancel}
          >
            Cancel
          </button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default PublishReportModal;
